// @flow

// 연결에 문제가 있을 때 Input 바로 위에 띄우는 띠 (#204).
// 번역자는 타이핑하면서 Input만 보고 있어서, 제목 옆 점 색이 바뀌는 것만으로는 놓친다.
// 정상일 때는 아무것도 보이지 않는다.
// - 빨강(끊김): 바로 띄운다
// - 노랑(재연결 중·느림): 잠깐 흔들리는 건 무시하고 몇 초 이상 이어질 때만 띄운다
// - 끊겼다가 복구되면 "다시 연결되었습니다"를 잠깐 보여 주고 사라진다

import React, { useEffect, useRef, useState } from "react";
import Alert from "react-bootstrap/Alert";
import { useConnectionView, formatRtt } from "./ConnectionIndicator.react";

export const DEGRADED_GRACE_MS = 5 * 1000;
export const RECOVERED_SHOW_MS = 3 * 1000;

type Shown = "none" | "down" | "degraded" | "recovered";

export default function ConnectionBanner(): React$Element<any> | null {
  const { status, send, level } = useConnectionView();
  const [shown, setShown] = useState<Shown>("none");
  const wasDown = useRef(false);

  useEffect(() => {
    if (level === "down") {
      wasDown.current = true;
      setShown("down");
      return;
    }
    if (level === "degraded") {
      const timer = setTimeout(() => setShown("degraded"), DEGRADED_GRACE_MS);
      // 노랑으로 넘어오는 동안 빨강 띠는 그대로 둔다 (깜빡이지 않게)
      if (!wasDown.current) {
        setShown("none");
      }
      return () => clearTimeout(timer);
    }
    if (level === "ok" && wasDown.current) {
      wasDown.current = false;
      setShown("recovered");
      const timer = setTimeout(() => setShown("none"), RECOVERED_SHOW_MS);
      return () => clearTimeout(timer);
    }
    if (level === "ok") {
      setShown("none");
    }
  }, [level]);

  if (shown === "none") {
    return null;
  }

  let variant;
  let text;
  if (shown === "down") {
    variant = "danger";
    text = status.online
      ? "서버와 연결이 끊겼습니다. 다시 연결하는 중입니다 — 지금 보내는 자막은 실패할 수 있고, 실패한 자막은 아래에서 재전송할 수 있습니다."
      : "인터넷 연결이 끊겼습니다 — 지금 보내는 자막은 실패할 수 있고, 실패한 자막은 아래에서 재전송할 수 있습니다.";
  } else if (shown === "recovered") {
    variant = "success";
    text = "다시 연결되었습니다.";
  } else if (
    status.appsyncRetrying > 0 ||
    status.mqttRetrying > 0 ||
    status.failures > 0
  ) {
    variant = "warning";
    text = "다시 연결하는 중입니다… 자막 표시가 잠시 늦을 수 있습니다.";
  } else if (send.retrying) {
    variant = "warning";
    text = "자막 전송을 다시 시도하는 중입니다.";
  } else {
    variant = "warning";
    text = `연결이 느립니다 (서버 응답 ${formatRtt(status.rttMs ?? 0)}).`;
  }

  return (
    <Alert
      variant={variant}
      className="py-2 px-3 mb-2"
      role="alert"
      data-testid="connection-banner"
      data-kind={shown}
    >
      {text}
    </Alert>
  );
}
